import React from 'react'
import { useTheme } from '@/contexts/ThemeContext'
import { cn } from '@/lib/utils'
import { useAnnouncer } from '@/lib/hooks/useAccessibility'

interface ThemeToggleProps {
  className?: string
  showLabel?: boolean
}

const ThemeToggle: React.FC<ThemeToggleProps> = ({ className, showLabel = false }) => {
  const { theme, toggleTheme } = useTheme()
  const announce = useAnnouncer()

  const handleToggle = () => {
    toggleTheme()
    const newTheme = theme === 'light' ? 'dark' : 'light'
    announce(`Switched to ${newTheme} mode`)
  }

  const buttonLabel = `Switch to ${theme === 'light' ? 'dark' : 'light'} mode`

  return (
    <button
      onClick={handleToggle}
      className={cn(
        'p-2 rounded-full transition-all duration-200',
        'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
        'hover:scale-105 active:scale-95',
        theme === 'dark'
          ? 'bg-gray-800 text-yellow-300 hover:bg-gray-700'
          : 'bg-gray-100 text-gray-800 hover:bg-gray-200',
        showLabel && 'flex items-center space-x-2 px-3',
        className
      )}
      aria-label={buttonLabel}
      title={buttonLabel}
    >
      {theme === 'dark' ? (
        // Sun icon for dark mode (clicking will switch to light)
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
            clipRule="evenodd"
          />
        </svg>
      ) : (
        // Moon icon for light mode (clicking will switch to dark)
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
        </svg>
      )}
      {showLabel && (
        <span className="text-sm font-medium">{theme === 'dark' ? 'Light' : 'Dark'} Mode</span>
      )}
    </button>
  )
}

export default ThemeToggle
